import type { ParseKeys } from "i18next";
import { useTranslation } from "react-i18next";
// src/pages/PurchaseEdit.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiGet, apiPut, apiPatch } from "../api/client";
import { ArrowLeftIcon, TrashIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { companyRoute } from "../utils/routeHelpers";
import { Button } from "../components/Button";
import { PurchaseInfoHeader } from "../components/PurchaseInfoHeader";
import { MemoSection } from "../components/MemoSection";
import { AppleCard } from "../components/AppleCard";
import { formatNumber, formatMoney } from "../utils/formatNumber";
import {
  canonicalLineTotal,
  deriveCostFromTotal,
  exceeds2dp,
} from "../utils/purchaseLineCalc";
import { A } from "../styles/appleClasses";
import { AppleComboBox } from "../components/AppleComboBox";
import { AppleDateInput } from "../components/AppleDateInput";
import { AppleNumberInput } from "../components/AppleNumberInput";
import { ymdTodayLocal } from "../utils/dateUtils";
import {
  extractApiDetailOutcome,
  formatClosedThroughDate,
  isClosedPeriodError,
  isDateClosed,
} from "../utils/closedPeriod";

// --- Types ---
interface Vendor {
  id: number | null;
  name: string;
  email: string;
}

interface PurchaseLine {
  id: number;
  product_id: number;
  product_name: string;
  qty: string;
  cost_price: string;
  disc_pct: string; // UI-only; backend uses "discount" (percent)
  sub_total: string; // UI-only; backend uses "total"
  lastEdited?: "qty" | "cost_price" | "disc_pct" | "sub_total";
  // PURCHASE-LINE-ROUND-STAGE-1 editable-total UX (same contract as
  // PurchaseEntry): totalError blocks submission; totalNotice is informational.
  totalError?: string;
  totalNotice?: string;
}

interface RawLineFromAPI {
  id: number;
  product_id: number;
  product_name: string;
  qty: number;
  cost_price: number;
  discount?: number; // percent
  total: number; // line subtotal
}

interface Purchase {
  id: number;
  date: string; // invoice date (ymd)
  vendor: Vendor | null;
  invoice_number?: string | null;
  lines: RawLineFromAPI[];
  amount: number;
  status: string | null;
  due_date?: string; // ymd
  memo?: string | null;
  voided?: boolean;
  edited?: boolean;
  edit_count?: number;
  last_edited_at?: string;

  // Needed to check whether this bill has payments
  bill_id?: number | string;
}

interface VendorPayment {
  id: number;
  amount: number | string;
  vendor_payment_date: string;
  bill_id: number | string;
  payment_account_id?: number;
}

interface ProductStock {
  id: number;
  stock_qty: number;
}

// ---- helpers ----
function formatCurrency(n: number | undefined | null) {
  return typeof n === "number" && !Number.isNaN(n) ? formatMoney(n) : "—";
}

const clamp = (n: number, min: number, max: number) =>
  Math.min(Math.max(n, min), max);

const round2 = (n: number) => Math.round(n * 100) / 100;

/** Show qty with no decimals unless user entered them (up to 2dp). */
function formatQtyDisplay(n: number): string {
  if (!isFinite(n)) return "";
  const s = round2(n).toString();
  if (!s.includes(".")) return s;
  return s.replace(/\.?0+$/, "");
}

/** Always money with two decimals. */
function formatMoneyDisplay(n: number): string {
  if (!isFinite(n)) return "";
  return round2(n).toFixed(2);
}

function parseNum(v: string): number {
  if (v === "" || v == null) return NaN;
  const n = Number(v);
  return isNaN(n) ? NaN : n;
}

function sanitizeNumeric(v: string): string {
  let s = v.replace(/[^\d.]/g, "");
  const firstDot = s.indexOf(".");
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, "");
  }
  return s;
}

function asNum(v: string | number | undefined | null, fallback = 0): number {
  if (typeof v === "number") return Number.isFinite(v) ? v : fallback;
  const n = parseNum(String(v ?? ""));
  return Number.isFinite(n) ? n : fallback;
}

function toErrorString(err: any) {
  const d = err?.response?.data?.detail;
  if (Array.isArray(d)) {
    return d
      .map((e: any) => {
        const loc = Array.isArray(e.loc) ? e.loc.join(".") : "field";
        return `${loc}: ${e.msg}`;
      })
      .join("\n");
  }
  if (typeof d === "string") return d;
  return err?.message || localMessage("purchases.errors.saveChanges");
}

// Display metadata is never persisted or passed to error classifiers.
type LocalPurchaseMessage = { key: ParseKeys; values?: Record<string, string | number>; closedDate?: string | null; field?: LocalPurchaseMessage; inner?: PurchaseMessage };
type PurchaseMessage = string | LocalPurchaseMessage;
const localMessage = (key: ParseKeys, values?: Record<string, string | number>): LocalPurchaseMessage => ({ key, values });
function localApiDetail(err: any): PurchaseMessage {
  const outcome = extractApiDetailOutcome(err);
  return outcome.kind === "raw" ? outcome.text : localMessage("sales.errors.generic");
}
function localClosedPeriod(closedDate?: string | null): LocalPurchaseMessage {
  return { key: "sales.errors.closedSubmit", closedDate };
}

// Provenance lives outside the line object: draft shape/raw strings stay intact.
const localLineMessages = new WeakSet<object>();
function markLocalLine<T extends object>(line: T): T {
  localLineMessages.add(line);
  return line;
}
function lineMessage(line: object, raw?: string): PurchaseMessage | undefined {
  if (!raw || !localLineMessages.has(line)) return raw;
  if (raw === "Total must be a valid amount with at most 2 decimal places.") return localMessage("purchases.errors.invalidTotal");
  if (raw === "At 100% discount the total must be 0.00.") return localMessage("purchases.errors.fullDiscount");
  const adjusted = /^Adjusted to (\d+\.\d{2})$/.exec(raw);
  return adjusted ? localMessage("purchases.errors.adjusted", { amount: adjusted[1] }) : raw;
}

class LocalPurchaseError extends Error {
  constructor(raw: string, readonly display: LocalPurchaseMessage) { super(raw); }
}

export default function PurchaseEdit() {
  const { t } = useTranslation();
  const messageText = (message: PurchaseMessage | null | undefined): string => {
    if (message == null) return "";
    if (typeof message === "string") return message;
    const values = "closedDate" in message
      ? { date: message.closedDate ? formatClosedThroughDate(message.closedDate) : t("sales.errors.closedDate") }
      : { ...message.values, ...(message.field ? { field: messageText(message.field) } : {}), ...(message.inner !== undefined ? { error: messageText(message.inner) } : {}) };
    return t(message.key, values);
  };

  const { id, company_id } = useParams<{ id: string; company_id: string }>();
  const navigate = useNavigate();

  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [vendorId, setVendorId] = useState<string>(""); // selected vendor id (string for ComboBox)

  const [purchase, setPurchase] = useState<Purchase | null>(null);
  const [lines, setLines] = useState<PurchaseLine[]>([]);
  const [memoDraft, setMemoDraft] = useState<string>("");

  // ✅ NEW: invoice meta
  const [invoiceDate, setInvoiceDate] = useState<string>(""); // ymd
  const [invoiceNumber, setInvoiceNumber] = useState<string>("");
  const [dueDate, setDueDate] = useState<string>(""); // ymd
  // True once the user edits the due-date field itself, so an untouched save
  // (or an invoice-date auto-follow) never repopulates a stored NULL.
  const dueDateDirtyRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<PurchaseMessage | null>(null);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<PurchaseMessage | null>(null);

  const [closedThroughDate, setClosedThroughDate] = useState<string | null>(
    null,
  );

  // Delete line modal
  const [showDelete, setShowDelete] = useState(false);
  const [deleteLineId, setDeleteLineId] = useState<number | null>(null);

  // Stock snapshot (to prevent negative stock when reducing/deleting)
  const [productsStock, setProductsStock] = useState<Record<number, number>>(
    {},
  );

  // Payments (lock editing if any exist)
  const [vendorPayments, setVendorPayments] = useState<VendorPayment[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState<boolean>(true);

  // Load vendors (for Vendor combobox)
  useEffect(() => {
    const base = company_id ? `/vendors?company_id=${company_id}` : `/vendors`;
    const url = `${base}${base.includes("?") ? "&" : "?"}_=${Date.now()}`;

    apiGet<Vendor[]>(url)
      .then((data) => setVendors(data || []))
      .catch(() => setVendors([]));
  }, [company_id]);

  // Load purchase
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setLoadError(null);
    setError(null);

    const base = company_id
      ? `/purchases/${id}?company_id=${company_id}`
      : `/purchases/${id}`;

    const url = `${base}${base.includes("?") ? "&" : "?"}_=${Date.now()}`;

    apiGet<Purchase>(url)
      .then((data) => {
        setPurchase(data);

        // ✅ invoice meta
        const inv = (data.date || "").trim();
        const due = (data.due_date || "").trim();
        setInvoiceDate(inv);
        setInvoiceNumber((data.invoice_number || "").trim());
        // A stored NULL due date renders as EMPTY (no invoice-date fallback),
        // so it is preserved unless the user explicitly sets/clears it.
        setDueDate(due);

        setMemoDraft(data.memo || "");
        setVendorId(data.vendor?.id != null ? String(data.vendor.id) : "");

        // Normalize incoming lines -> UI model
        const uiLines: PurchaseLine[] = data.lines.map((line) => {
          const disc =
            typeof (line as any).disc_pct === "number"
              ? (line as any).disc_pct
              : typeof line.discount === "number"
                ? line.discount
                : 0;

          // Prefer the backend-persisted total; only as a last resort compute
          // it via the canonical net-unit-first helper (never round-once).
          const canonFallback = canonicalLineTotal(
            String(line.qty),
            String(line.cost_price),
            String(disc),
          );
          const subtotal =
            typeof (line as any).sub_total === "number"
              ? (line as any).sub_total
              : typeof line.total === "number"
                ? line.total
                : canonFallback.ok
                  ? Number(canonFallback.totalText)
                  : 0;

          return {
            id: line.id,
            product_id: line.product_id,
            product_name: line.product_name,
            qty: formatQtyDisplay(line.qty),
            cost_price: formatQtyDisplay(line.cost_price),
            disc_pct: formatQtyDisplay(disc),
            sub_total: formatMoneyDisplay(subtotal),
            lastEdited: "disc_pct",
          };
        });

        setLines(uiLines);

        // Prefetch current stock snapshot
        const productIds = data.lines.map((l) => l.product_id);
        if (productIds.length) {
          const baseStock = company_id
            ? `/products/stock?company_id=${company_id}&ids=${productIds.join(
                ",",
              )}`
            : `/products/stock?ids=${productIds.join(",")}`;

          const stockUrl = `${baseStock}${
            baseStock.includes("?") ? "&" : "?"
          }_=${Date.now()}`;

          apiGet<ProductStock[]>(stockUrl)
            .then((stocks) => {
              const stockMap: Record<number, number> = {};
              stocks.forEach((p) => (stockMap[p.id] = p.stock_qty));
              setProductsStock(stockMap);
            })
            .catch(() => setProductsStock({}));
        }
      })
      .catch(() => setLoadError(localMessage("purchases.errors.load")))
      .finally(() => setLoading(false));
  }, [id, company_id]);

  useEffect(() => {
    if (!company_id) return;

    apiGet<{
      closed_through_date?: string | null;
    }>(`/settings/financial-year-end?company_id=${company_id}`)
      .then((res) => {
        setClosedThroughDate(res?.closed_through_date || null);
      })
      .catch(() => {
        setClosedThroughDate(null);
      });
  }, [company_id]);

  // Fetch payments for this bill (editing allowed only if none exist)
  useEffect(() => {
    if (!purchase) return;

    const billId = String(purchase.bill_id ?? purchase.id ?? "");
    if (!billId) {
      setVendorPayments([]);
      setPaymentsLoading(false);
      return;
    }

    setPaymentsLoading(true);

    const base = company_id
      ? `/ar-ap/vendor-payments?company_id=${company_id}`
      : `/ar-ap/vendor-payments`;

    const url = `${base}${base.includes("?") ? "&" : "?"}_=${Date.now()}`;

    apiGet<VendorPayment[]>(url)
      .then((all) => {
        const billPayments = (all || []).filter(
          (p) => String(p.bill_id) === billId,
        );
        setVendorPayments(billPayments);
      })
      .catch(() => setVendorPayments([]))
      .finally(() => setPaymentsLoading(false));
  }, [purchase?.bill_id, purchase?.id, company_id, purchase]);

  const hasPayments = vendorPayments.length > 0;

  // ✅ Sum of payments (for status bubble: Unpaid / Partial / Paid)
  const paidAmount = useMemo(() => {
    return round2(
      (vendorPayments || []).reduce((sum, p) => {
        const n = typeof p.amount === "string" ? Number(p.amount) : p.amount;
        return sum + (Number.isFinite(n) ? (n as number) : 0);
      }, 0),
    );
  }, [vendorPayments]);

  const grandTotal = useMemo(
    () => round2(lines.reduce((sum, l) => sum + asNum(l.sub_total, 0), 0)),
    [lines],
  );

  const outstanding = useMemo(() => {
    return Math.max(0, round2(grandTotal - paidAmount));
  }, [grandTotal, paidAmount]);

  const payStatus = useMemo<"unpaid" | "partial" | "paid" | "voided">(() => {
    if (!purchase) return "unpaid";
    if (purchase.voided) return "voided";
    const eps = 0.01; // cents tolerance
    if (paidAmount <= eps) return "unpaid";
    if (paidAmount + eps < grandTotal) return "partial";
    return "paid";
  }, [purchase, paidAmount, grandTotal]);

  // ✅ New rule: only lock if voided OR payments exist.
  const locked = useMemo(() => {
    if (!purchase) return true;
    if (purchase.voided) return true;
    if (paymentsLoading) return true; // lock while checking
    if (hasPayments) return true;
    return false;
  }, [purchase, paymentsLoading, hasPayments]);

  const lockReason = useMemo(() => {
    if (!purchase) return "";
    if (purchase.voided) return localMessage("purchases.edit.voided");
    if (paymentsLoading) return localMessage("purchases.edit.checkingPayments");
    if (hasPayments)
      return localMessage("purchases.edit.paymentLock");
    return "";
  }, [purchase, paymentsLoading, hasPayments]);

  // AppleComboBox requires id: string | number (no null)
  const vendorOptions = useMemo(() => {
    return (vendors || []).filter(
      (v): v is Vendor & { id: number } => v.id != null,
    );
  }, [vendors]);

  const selectedVendor = useMemo(() => {
    return vendors.find((v) => String(v.id) === String(vendorId)) ?? null;
  }, [vendors, vendorId]);

  // Under the standardized due-date policy the due date is a first-class,
  // optional field: it is shown for any editable (non-voided) purchase that has
  // an invoice date, so it can be viewed, changed, or deliberately cleared --
  // including the common case where the default equals the invoice date. (The
  // previous heuristic hid the field whenever due == invoice date, which the
  // new "due on receipt" default makes the common case.)
  const showDueDate = useMemo(() => {
    const inv = (invoiceDate || "").trim();
    if (!inv) return false;
    if (purchase?.voided) return false;
    return true;
  }, [invoiceDate, purchase?.voided]);

  const isClosedDate = useMemo(() => {
    return isDateClosed(invoiceDate, closedThroughDate);
  }, [invoiceDate, closedThroughDate]);

  function wouldGoNegative(
    product_id: number,
    oldQty: string | number,
    newQty: string | number,
  ) {
    const stockNow = productsStock[product_id] ?? 0;
    const oldQtyNum = asNum(oldQty, 0);
    const newQtyNum = asNum(newQty, 0);
    const simulatedStock = stockNow - (newQtyNum - oldQtyNum);
    return simulatedStock < 0;
  }

  function handleLineChange(
    idx: number,
    field: keyof PurchaseLine,
    raw: string,
  ) {
    // PURCHASE-LINE-ROUND-STAGE-1: preserve the RAW typed text. Do NOT
    // sanitizeNumeric here -- stripping "e"/","/"+" would silently turn
    // malformed input ("1e2") into a plausible value ("12") before the strict
    // grammar/precision validator (canonicalLineTotal / deriveCostFromTotal /
    // submit) can inspect it. Malformed / >2dp stays visible and intact and is
    // rejected downstream; the wouldGoNegative guard below tolerates a
    // non-numeric value (asNum falls back to 0).
    const clean = raw;

    setLines((old) =>
      old.map((line, i) => {
        if (i !== idx) return line;

        let qty = line.qty;
        let cost_price = line.cost_price;
        let disc_pct = line.disc_pct;
        let sub_total = line.sub_total;

        if (field === "qty") {
          if (
            clean !== "" &&
            !isNaN(parseNum(clean)) &&
            asNum(clean, 0) < asNum(line.qty, 0) &&
            wouldGoNegative(line.product_id, line.qty, clean)
          ) {
            setError(
              localMessage("purchases.errors.reduceUsedStock", { name: line.product_name }),
            );
            setTimeout(() => setError(null), 3500);
            return line;
          }
          qty = clean;
        } else if (field === "cost_price") {
          cost_price = clean;
        } else if (field === "disc_pct") {
          disc_pct = clean;
        } else if (field === "sub_total") {
          sub_total = clean;
        } else {
          return line;
        }

        const discStr = (disc_pct ?? "").trim() === "" ? "0" : disc_pct;
        let totalError: string | undefined;
        let totalNotice: string | undefined;
        if (field === "qty" || field === "cost_price" || field === "disc_pct") {
          // PURCHASE-LINE-ROUND-STAGE-1: canonical net-unit-first total via
          // exact BigInt arithmetic (matches the backend). A non-total edit
          // re-derives the total from cost and clears any total-entry state.
          const r = canonicalLineTotal(qty, cost_price, discStr);
          if (r.ok) sub_total = formatMoneyDisplay(Number(r.totalText));
        } else if (field === "sub_total") {
          // Solve a canonical 2dp cost from the entered total, discount held
          // fixed (never reverse-derived). Fully validated; never throws.
          const invd = deriveCostFromTotal(sub_total, qty, discStr);
          if (!invd.ok) {
            totalError =
              "Total must be a valid amount with at most 2 decimal places.";
          } else if (invd.action === "derive_cost") {
            cost_price = invd.costText;
            sub_total = formatMoneyDisplay(Number(invd.canonicalTotalText));
            if (!invd.exact) {
              totalNotice = `Adjusted to ${invd.canonicalTotalText}`;
            }
          } else if (invd.exact) {
            // 100% discount, entered 0.00 -> preserve cost, accept 0.00.
            sub_total = formatMoneyDisplay(Number(invd.canonicalTotalText));
          } else {
            // 100% discount, nonzero total: not representable. Keep the raw
            // entered text, error, block submission -- never replace with 0.00.
            totalError = "At 100% discount the total must be 0.00.";
          }
        }

        let lastEdited = line.lastEdited;
        if (field === "disc_pct") lastEdited = "disc_pct";
        if (field === "sub_total") lastEdited = "sub_total";

        return markLocalLine({
          ...line,
          qty,
          cost_price,
          disc_pct,
          sub_total,
          lastEdited,
          totalError,
          totalNotice,
        });
      }),
    );
  }

  function confirmDeleteLine(lineId: number) {
    setDeleteLineId(lineId);
    setShowDelete(true);
  }

  function handleDeleteLine() {
    const line = lines.find((l) => l.id === deleteLineId);
    if (line) {
      if (wouldGoNegative(line.product_id, line.qty, 0)) {
        setError(
          localMessage("purchases.errors.deleteUsedStock", { name: line.product_name }),
        );
        setShowDelete(false);
        setTimeout(() => setError(null), 3500);
        return;
      }
    }
    setLines((old) => old.filter((l) => l.id !== deleteLineId));
    setShowDelete(false);
    setDeleteLineId(null);
  }

  async function handleSave() {
    if (!purchase) return;
    setSaving(true);
    setError(null);

    const inv = (invoiceDate || "").trim();
    if (!inv) {
      setError(localMessage("purchases.errors.invoiceRequired"));
      setSaving(false);
      return;
    }

    // If due date is shown (terms), the due date is optional (a deliberate
    // clear = "no due date"); enforce due >= invoice date only when present.
    if (showDueDate) {
      const due = (dueDate || "").trim();
      if (due && due < inv) {
        setError(localMessage("purchases.errors.editDueDate"));
        setSaving(false);
        return;
      }
    }

    // Final stock guard
    for (const line of lines) {
      const origLine = purchase.lines.find((l) => l.id === line.id);
      if (!origLine) continue;
      if (
        asNum(line.qty, 0) < origLine.qty &&
        wouldGoNegative(line.product_id, origLine.qty, line.qty)
      ) {
        setError(
          localMessage("purchases.errors.reduceUsedStock", { name: line.product_name }),
        );
        setSaving(false);
        setTimeout(() => setError(null), 3500);
        return;
      }
    }

    try {
      const parsedLines = lines.map((l) => {
        // PURCHASE-LINE-ROUND-STAGE-1: never post while a blocking total error
        // remains on the line (invalid/>2dp total, or nonzero @ 100% discount).
        if (l.totalError) {
          throw new LocalPurchaseError(`Total for "${l.product_name}": ${l.totalError}`, { ...localMessage("purchases.errors.lineTotal", { name: l.product_name }), inner: lineMessage(l, l.totalError) });
        }

        // Validate the RAW state strings FIRST -- exceeds2dp enforces the strict
        // grammar (rejects "1e2", "1,000", "+1", malformed) AND >2dp. Do NOT
        // sanitizeNumeric before this: sanitization would silently turn an
        // invalid value into a valid-looking one (e.g. "1e2" -> "12").
        const discRaw = (l.disc_pct ?? "").trim() === "" ? "0" : l.disc_pct;
        if (exceeds2dp(l.qty)) {
          throw new LocalPurchaseError(`Quantity for "${l.product_name}" must be a valid number with at most 2 decimal places.`, localMessage("purchases.errors.lineQuantity", { name: l.product_name }));
        }
        if (exceeds2dp(l.cost_price)) {
          throw new LocalPurchaseError(`Unit cost for "${l.product_name}" must be a valid number with at most 2 decimal places.`, localMessage("purchases.errors.lineCost", { name: l.product_name }));
        }
        if (exceeds2dp(discRaw)) {
          throw new LocalPurchaseError(`Discount for "${l.product_name}" must be a valid number with at most 2 decimal places.`, localMessage("purchases.errors.lineDiscount", { name: l.product_name }));
        }

        // Only AFTER the raw grammar is accepted do we convert for numeric
        // bound checks (sanitizeNumeric here is a no-op on already-accepted
        // input; parse cannot resurrect a rejected value).
        const qtyNum = parseNum(sanitizeNumeric(l.qty));
        const costNum = parseNum(sanitizeNumeric(l.cost_price));
        const discNum = parseNum(sanitizeNumeric(discRaw));

        if (!Number.isFinite(qtyNum) || qtyNum <= 0) {
          throw new LocalPurchaseError(`Invalid quantity for "${l.product_name}".`, localMessage("purchases.errors.invalidQuantity", { name: l.product_name }));
        }
        if (!Number.isFinite(costNum) || costNum < 0) {
          throw new LocalPurchaseError(`Invalid unit cost for "${l.product_name}".`, localMessage("purchases.errors.invalidCost", { name: l.product_name }));
        }
        if (!Number.isFinite(discNum) || discNum < 0 || discNum > 100) {
          throw new LocalPurchaseError(`Invalid discount for "${l.product_name}".`, localMessage("purchases.errors.invalidDiscount", { name: l.product_name }));
        }

        // When total was the last field edited, re-confirm the RAW total-entry
        // state (again, never sanitized first) before trusting the canonical
        // payload derived from cost.
        if (l.lastEdited === "sub_total") {
          const invd = deriveCostFromTotal(l.sub_total, l.qty, discRaw);
          if (!invd.ok) {
            throw new LocalPurchaseError(`Total for "${l.product_name}" must be a valid amount with at most 2 decimal places.`, localMessage("purchases.errors.lineTotalPrecision", { name: l.product_name }));
          }
          if (invd.action === "preserve_cost" && !invd.exact) {
            throw new LocalPurchaseError(`Total for "${l.product_name}" must be 0.00 at 100% discount.`, localMessage("purchases.errors.lineTotalDiscount", { name: l.product_name }));
          }
        }

        const calc = canonicalLineTotal(l.qty, l.cost_price, discRaw);
        const canonicalTotal = calc.ok ? Number(calc.totalText) : 0;

        return {
          id: l.id,
          product_id: l.product_id,
          qty: qtyNum,
          cost_price: costNum,
          discount: discNum,
          total: canonicalTotal,
        };
      });

      const payload: any = {
        date: inv,
        vendor_id: vendorId ? Number(vendorId) : null,
        invoice_number: invoiceNumber.trim() || null,
        lines: parsedLines,
      };

      // Due-date policy (edit): only send due_date when the user actually
      // edited the field. Untouched -> omit -> the backend leaves the stored
      // value untouched (a historical NULL is never repopulated, and an
      // invoice-date auto-follow never persists). Touched -> the chosen date,
      // or an explicit null on a deliberate clear.
      if (showDueDate && dueDateDirtyRef.current) {
        const trimmed = (dueDate || "").trim();
        payload.due_date = trimmed ? trimmed : null;
      }

      // ✅ Save memo first
      const currentMemo = purchase.memo || "";
      const nextMemo = memoDraft || "";
      if (nextMemo !== currentMemo) {
        const memoUrl = company_id
          ? `/purchases/${purchase.id}/memo?company_id=${company_id}`
          : `/purchases/${purchase.id}/memo`;

        await apiPatch(memoUrl, { memo: nextMemo });
        setPurchase((prev) => (prev ? { ...prev, memo: nextMemo } : prev));
      }

      const putUrl = company_id
        ? `/purchases/${purchase.id}?company_id=${company_id}`
        : `/purchases/${purchase.id}`;

      await apiPut(putUrl, payload);

      navigate(companyRoute(company_id!, `/purchases/${purchase.id}`), {
        replace: true,
      });
    } catch (err: any) {
      if (isClosedPeriodError(err)) {
        setError(localClosedPeriod(closedThroughDate));
        return;
      }

      if (err instanceof Error && /^Invalid /.test(err.message)) {
        setError(err instanceof LocalPurchaseError ? err.display : err.message);
        return;
      }
      setError(err instanceof LocalPurchaseError ? err.display : localApiDetail(err) || toErrorString(err));
    } finally {
      setSaving(false);
    }
  }

  // --- blur formatters ---
  function onBlurQty(idx: number, e: React.FocusEvent<HTMLInputElement>) {
    // PURCHASE-LINE-ROUND-STAGE-1: validate the RAW input against the strict
    // grammar BEFORE any sanitization -- never silently round >2dp, and never
    // let sanitizeNumeric turn a malformed value ("1e2", "1,000", "+1") into a
    // valid one. Malformed/>2dp is preserved so submit rejects it.
    const raw = e.currentTarget.value;
    if (exceeds2dp(raw)) {
      handleLineChange(idx, "qty", raw);
      return;
    }
    const v = parseNum(sanitizeNumeric(raw));
    if (isNaN(v)) {
      handleLineChange(idx, "qty", raw);
      return;
    }
    handleLineChange(idx, "qty", formatQtyDisplay(Math.max(0, v)));
  }

  function onBlurMoney(
    idx: number,
    field: "cost_price" | "sub_total",
    e: React.FocusEvent<HTMLInputElement>,
  ) {
    // PURCHASE-LINE-ROUND-STAGE-1:
    //  * sub_total blur is a NO-OP -- the value is fully validated and (when
    //    representable) canonicalized on every change; re-running it on blur
    //    would clear the "Adjusted to X" notice, re-round it, or drop a
    //    total error. (Matches PurchaseEntry, whose total blur also no-ops.)
    //  * cost_price blur passes the RAW typed value straight through (same as
    //    onChange). It must never sanitize a malformed value into validity or
    //    round >2dp; excess-precision/malformed cost is preserved and rejected
    //    at submit.
    if (field === "sub_total") return;
    handleLineChange(idx, field, e.currentTarget.value);
  }

  if (loading)
    return (
      <div className={`text-center py-8 ${A.body} text-gray-500`}>
        {t("purchases.edit.loading")}
      </div>
    );

  if (loadError)
    return (
      <div className={`text-center py-8 ${A.body} text-red-500`}>
        {messageText(loadError)}
      </div>
    );

  if (!purchase)
    return (
      <div className={`text-center py-8 ${A.body} text-gray-500`}>
        {t("purchases.edit.notFound")}
      </div>
    );

  const cellInput = `${A.inputBase} px-3 text-right font-medium ${A.num} shadow-none appearance-none`;
  const metaInput = `${A.inputBase} appearance-none`;

  return (
    <div
      className={`min-h-screen bg-gray-50 flex flex-col items-center pt-6 pb-10 px-2 ${A.body}`}
    >
      {/* Header (match Sale edit layout + Exit X) */}
      <div className="sticky top-0 z-30 bg-gray-50 w-full max-w-3xl flex items-center border-b border-gray-100 px-2 pb-2 mb-2">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className={`${A.iconBtn} flex items-center justify-center hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD`}
          aria-label={t("directory.common.back")}
        >
          <ArrowLeftIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
        </button>

        <h2 className={`${A.title} flex-1 text-center`}>{t("purchases.edit.title")}</h2>

        <button
          type="button"
          onClick={() => navigate(companyRoute(company_id!, "/"))}
          className={`${A.iconBtn} flex items-center justify-center hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD`}
          aria-label={t("directory.common.close")}
        >
          <XMarkIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
        </button>
      </div>

      {/* Card */}
      <div className={`${A.card} w-full max-w-3xl mx-auto px-3 sm:px-6 py-5`}>
        <PurchaseInfoHeader
          purchaseLabels={t}
          date={invoiceDate || purchase.date}
          invoiceNumber={invoiceNumber || purchase.invoice_number || ""}
          vendor={selectedVendor?.name ?? purchase.vendor?.name}
          email={selectedVendor?.email ?? purchase.vendor?.email}
          statusBubbles={
            payStatus === "voided"
              ? [{ label: "Voided", bubbleType: "voided" }]
              : payStatus === "paid"
                ? [{ label: "Paid", bubbleType: "paid" }]
                : payStatus === "partial"
                  ? [{ label: "Partial", bubbleType: "partial" }]
                  : [{ label: "Unpaid", bubbleType: "unpaid" }]
          }
          dueDate={showDueDate ? dueDate : undefined}
          total={grandTotal}
          outstanding={outstanding}
          isPaid={payStatus === "paid"}
          edited={purchase.edited && !purchase.voided}
          lastEditedAt={purchase.last_edited_at}
        />

        {locked && (
          <div className={`${A.label} mt-1 text-gray-500`}>
            {t("purchases.edit.lockedPrefix")} {messageText(lockReason)}
          </div>
        )}

        {/* Vendor / Supplier Invoice No */}
        <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-12">
          <div className="md:col-span-6">
            <AppleComboBox
              emptyText={t("sales.shared.noResults")}
              toggleOptionsLabel={t("sales.shared.toggleOptions")}
              label={t("directory.vendors.singular")}
              options={vendorOptions}
              value={
                vendorOptions.find((v) => String(v.id) === String(vendorId)) ??
                null
              }
              onChange={(v) => setVendorId(v ? String(v.id) : "")}
              getOptionLabel={(v) => v.name}
              placeholder={t("purchases.edit.selectVendor")}
              disabled={locked || saving}
            />
          </div>

          <div className="md:col-span-6">
            <label className={`block ${A.label} font-medium mb-1`}>
              {t("purchases.common.supplierInvoice")}
            </label>
            <input
              type="text"
              className={metaInput}
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              placeholder={t("purchases.edit.optional")}
              disabled={locked || saving}
            />
          </div>
        </div>

        {/* Invoice Date / Due Date */}
        <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-12">
          <div className="md:col-span-6">
            <label className={`block ${A.label} font-medium mb-1`}>
              {t("purchases.common.invoiceDate")}
            </label>
            <AppleDateInput
              openCalendarLabel={t("sales.shared.openCalendar")}
              valueYmd={invoiceDate}
              onChangeYmd={(v) => {
                const prevInvoiceDate = invoiceDate;
                setInvoiceDate(v);

                if (!dueDate || dueDate === prevInvoiceDate) {
                  setDueDate(v);
                }
              }}
              allowEmpty={false}
              maxYmd={ymdTodayLocal()}
              disabled={locked || saving}
              ariaLabel={t("purchases.edit.invoiceDateAria")}
            />
          </div>

          <div className="md:col-span-6">
            <label className={`block ${A.label} font-medium mb-1`}>
              {t("sales.common.dueDate")}
            </label>
            <AppleDateInput
              openCalendarLabel={t("sales.shared.openCalendar")}
              valueYmd={dueDate}
              onChangeYmd={(ymd) => {
                dueDateDirtyRef.current = true;
                setDueDate(ymd);
              }}
              allowEmpty
              minYmd={invoiceDate || undefined}
              disabled={locked || saving}
              ariaLabel={t("sales.common.dueDateAria")}
            />
            <p className="mt-1 text-[11px] text-gray-400">
              {t("purchases.edit.dueHelp")}
            </p>
          </div>
        </div>

        {isClosedDate && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-800">
            {t("sales.errors.closedWarning")}
          </div>
        )}

        {/* --- Lines Table --- */}
        {/* DESKTOP */}
        <div className="hidden md:block mb-5">
          <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
            <table className="w-full text-[13px] text-gray-700">
              <thead className="bg-gray-50">
                <tr className="text-xs text-gray-500">
                  <th className="text-left px-3 py-2 font-normal">{t("directory.products.product")}</th>
                  <th className="text-right px-3 py-2 font-normal">{t("sales.common.qty")}</th>
                  <th className="text-right px-3 py-2 font-normal">
                    {t("purchases.edit.unitCost")}
                  </th>
                  <th className="text-right px-3 py-2 font-normal">{t("purchases.edit.discountPercent")}</th>
                  <th className="text-right px-3 py-2 font-normal">
                    {t("purchases.edit.subtotal")}
                  </th>
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {lines.map((line, idx) => (
                  <tr key={line.id}>
                    <td className="px-3 py-2 font-medium">
                      {line.product_name}
                    </td>

                    <td className="px-3 py-2">
                      <AppleNumberInput
                        value={line.qty}
                        disabled={locked}
                        data-testid={`edit-qty-${idx}`}
                        onBlur={(e) => onBlurQty(idx, e)}
                        onChange={(e) =>
                          handleLineChange(idx, "qty", e.target.value)
                        }
                        className={`${cellInput} w-24`}
                        inputMode="decimal"
                      />
                    </td>

                    <td className="px-3 py-2">
                      <AppleNumberInput
                        value={line.cost_price}
                        disabled={locked}
                        data-testid={`edit-cost-${idx}`}
                        onBlur={(e) => onBlurMoney(idx, "cost_price", e)}
                        onChange={(e) =>
                          handleLineChange(idx, "cost_price", e.target.value)
                        }
                        className={`${cellInput} w-28`}
                        inputMode="decimal"
                      />
                    </td>

                    <td className="px-3 py-2">
                      <AppleNumberInput
                        value={line.disc_pct}
                        disabled={locked}
                        data-testid={`edit-disc-${idx}`}
                        onBlur={(e) => {
                          // Validate RAW grammar before sanitize; no silent 2dp
                          // rounding and no sanitize-into-validity on blur.
                          const raw = e.currentTarget.value;
                          if (exceeds2dp(raw)) {
                            handleLineChange(idx, "disc_pct", raw);
                            return;
                          }
                          const n = parseNum(sanitizeNumeric(raw));
                          const clamped = isNaN(n) ? 0 : clamp(n, 0, 100);
                          handleLineChange(idx, "disc_pct", String(clamped));
                        }}
                        onChange={(e) =>
                          handleLineChange(idx, "disc_pct", e.target.value)
                        }
                        className={`${cellInput} w-24`}
                        inputMode="decimal"
                      />
                    </td>

                    <td className="px-3 py-2">
                      <AppleNumberInput
                        value={line.sub_total}
                        disabled={locked}
                        data-testid={`edit-subtotal-${idx}`}
                        onBlur={(e) => onBlurMoney(idx, "sub_total", e)}
                        onChange={(e) =>
                          handleLineChange(idx, "sub_total", e.target.value)
                        }
                        className={`${cellInput} w-28`}
                        inputMode="decimal"
                      />
                      {line.totalError ? (
                        <div className="mt-1 text-xs text-red-600">
                          {messageText(lineMessage(line, line.totalError))}
                        </div>
                      ) : line.totalNotice ? (
                        <div className="mt-1 text-xs text-gray-500">
                          {messageText(lineMessage(line, line.totalNotice))}
                        </div>
                      ) : null}
                    </td>

                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        className={`${A.iconBtn} h-9 w-9 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed`}
                        onClick={() => confirmDeleteLine(line.id)}
                        disabled={locked}
                        title={t("purchases.edit.deleteLine")}
                        aria-label={t("purchases.edit.deleteLine")}
                      >
                        <TrashIcon className="w-5 h-5 text-gray-400 hover:text-gray-600" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* MOBILE */}
        <div className="md:hidden mb-5">
          {lines.map((line, idx) => (
            <div
              key={line.id}
              className="mb-4 rounded-2xl border border-gray-200 bg-white p-4"
            >
              <div className="text-[13px] font-medium text-gray-700 mb-2">
                {line.product_name}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={A.label + " mb-1 block"}>{t("sales.common.qty")}</label>
                  <AppleNumberInput
                    value={line.qty}
                    disabled={locked}
                    onBlur={(e) => onBlurQty(idx, e)}
                    onChange={(e) =>
                      handleLineChange(idx, "qty", e.target.value)
                    }
                    className={`${cellInput} w-full`}
                    inputMode="decimal"
                  />
                </div>

                <div>
                  <label className={A.label + " mb-1 block"}>{t("purchases.edit.unitCost")}</label>
                  <AppleNumberInput
                    value={line.cost_price}
                    disabled={locked}
                    onBlur={(e) => onBlurMoney(idx, "cost_price", e)}
                    onChange={(e) =>
                      handleLineChange(idx, "cost_price", e.target.value)
                    }
                    className={`${cellInput} w-full`}
                    inputMode="decimal"
                  />
                </div>

                <div>
                  <label className={A.label + " mb-1 block"}>{t("purchases.edit.discountPercent")}</label>
                  <AppleNumberInput
                    value={line.disc_pct}
                    disabled={locked}
                    onBlur={(e) => {
                      // Validate RAW grammar before sanitize; no silent 2dp
                      // rounding and no sanitize-into-validity on blur.
                      const raw = e.currentTarget.value;
                      if (exceeds2dp(raw)) {
                        handleLineChange(idx, "disc_pct", raw);
                        return;
                      }
                      const n = parseNum(sanitizeNumeric(raw));
                      const clamped = isNaN(n) ? 0 : clamp(n, 0, 100);
                      handleLineChange(idx, "disc_pct", String(clamped));
                    }}
                    onChange={(e) =>
                      handleLineChange(idx, "disc_pct", e.target.value)
                    }
                    className={`${cellInput} w-full`}
                    inputMode="decimal"
                  />
                </div>

                <div>
                  <label className={A.label + " mb-1 block"}>{t("purchases.edit.subtotal")}</label>
                  <AppleNumberInput
                    value={line.sub_total}
                    disabled={locked}
                    onBlur={(e) => onBlurMoney(idx, "sub_total", e)}
                    onChange={(e) =>
                      handleLineChange(idx, "sub_total", e.target.value)
                    }
                    className={`${cellInput} w-full`}
                    inputMode="decimal"
                  />
                  {line.totalError ? (
                    <div className="mt-1 text-xs text-red-600">
                      {messageText(lineMessage(line, line.totalError))}
                    </div>
                  ) : line.totalNotice ? (
                    <div className="mt-1 text-xs text-gray-500">
                      {messageText(lineMessage(line, line.totalNotice))}
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="flex justify-end mt-3">
                <button
                  type="button"
                  className={`${A.iconBtn} hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed`}
                  onClick={() => confirmDeleteLine(line.id)}
                  disabled={locked}
                  title={t("purchases.edit.deleteLine")}
                  aria-label={t("purchases.edit.deleteLine")}
                >
                  <TrashIcon className="w-5 h-5 text-gray-400 hover:text-gray-600" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* ✅ Memo (always editable on edit page; no pencil) */}
        <AppleCard padding="p-4" className="mb-6">
          <MemoSection
            mode="edit"
            variant="plain"
            label={t("sales.common.memo")}
            value={memoDraft}
            onChange={setMemoDraft}
            rows={2}
            disabled={locked || saving}
            placeholder={t("sales.common.memoPlaceholder")}
          />
        </AppleCard>

        {/* Grand Total */}
        <div className="mb-4 text-right">
          <span className="text-[15px] font-semibold tabular-nums text-gray-700">
            {t("sales.common.totalWithColon")} {formatMoney(grandTotal)}
          </span>
        </div>

        {/* ✅ Actions */}
        <div className="flex gap-3 justify-end mt-6">
          <Button
            variant="secondary"
            type="button"
            onClick={() =>
              navigate(companyRoute(company_id!, `/purchases/${purchase.id}`))
            }
            disabled={saving}
          >
            {t("directory.common.cancel")}
          </Button>

          <Button
            variant="primary"
            type="button"
            onClick={handleSave}
            disabled={saving || locked}
          >
            {saving ? t("directory.common.savingDots") : t("directory.common.save")}
          </Button>
        </div>

        {/* Delete Confirmation */}
        {showDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm px-3">
            <div className="bg-white rounded-2xl shadow-md ring-1 ring-black/5 p-6 max-w-xs w-full">
              <TrashIcon className="w-10 h-10 text-red-400 mb-2 mx-auto" />
              <div className="text-[15px] font-semibold mb-3 text-gray-700 text-center">
                {t("purchases.edit.deleteLineConfirm")}
              </div>
              <div className="mb-5 text-gray-500 text-[13px] text-center">
                {t("purchases.edit.deleteLineWarning")}
              </div>
              <div className="flex justify-end gap-3">
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => setShowDelete(false)}
                >
                  {t("directory.common.cancel")}
                </Button>
                <Button
                  variant="primary"
                  type="button"
                  className="bg-red-600 hover:bg-red-700"
                  onClick={handleDeleteLine}
                  disabled={locked}
                >
                  {t("directory.common.delete")}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Inline errors */}
        {error && (
          <div className="mt-4 text-center text-[13px] text-red-600 whitespace-pre-wrap">
            {messageText(error)}
          </div>
        )}
      </div>
    </div>
  );
}
